import React, { useContext } from "react";
import { Listbox as HeadlessListBox, ListboxOptionsProps } from "@headlessui/react";
import { useColorSchemeFn } from "@ugitgud/legos/hooks/useColorScheme";
import { WithClassName, WithColorScheme, WithPortal } from "@ugitgud/legos/ui/shared";
import { DropdownColorSchemes } from "@ugitgud/legos/ui/form/dropdown/types";
import { Placement, size, useFloating, useMergeRefs } from "@floating-ui/react";
import { DropdownContext } from "@ugitgud/legos/ui/form/dropdown/Dropdown";
import { createPortal } from "react-dom";

export type DropdownOptionsProps = WithClassName<WithColorScheme<ListboxOptionsProps<"ul">, DropdownColorSchemes>> &
  WithPortal & {
    placement?: Placement;
  };

export const DropdownOptions = React.forwardRef<HTMLUListElement, DropdownOptionsProps>(function (
  { className, colorScheme, portal, placement = "bottom-start", ...rest }: DropdownOptionsProps,
  ref: React.ForwardedRef<HTMLUListElement>,
) {
  const { anchor } = useContext(DropdownContext);
  const { refs: floatingRefs, floatingStyles } = useFloating({
    placement: placement,
    elements: {
      reference: anchor,
    },
    strategy: "absolute",
    middleware: [
      size({
        apply: ({ elements }) => {
          Object.assign(elements.floating.style, {
            "min-width": `${elements.reference.getBoundingClientRect().width}px`,
          });
        },
      }),
    ],
  });

  const mergedRefs = useMergeRefs([ref, floatingRefs.setFloating]);

  const scheme = useColorSchemeFn({
    classes: {
      secondary: "dark:bg-purple-100",
    },
    common: "absolute w-max z-[999] shadow-md mt-2 overflow-auto rounded-sm focus:outline-0 focus-visible:outline-none",
    default: "secondary",
    className: className,
    scheme: colorScheme,
  });

  const content = <HeadlessListBox.Options ref={mergedRefs} className={scheme} {...rest} style={floatingStyles} />;
  return portal ? createPortal(content, portal) : content;
});
